// /hr/dpp a /hr/naklady — roční přehledy po měsících (owner only).
//
// Jedna komponenta, dva režimy podle propu `typ`:
//   dpp     ... odpracované hodiny na DPP + přepínač "má aktivní DPP"
//   naklady ... celkový náklad na člověka v tisících Kč (22000 Kč = 22),
//               včetně skrytých položek (bokem, odečty, DPP)
//
// Tabulka je plně ručně editovatelná (klik do buňky). U měsíců, kde už appka
// počítá výplaty (od 06/2026), jde jedním tlačítkem předvyplnit celý měsíc.
// Ukončené lidi lze skrýt, data zůstávají a jdou jedním přepínačem odkrýt.

import React, { useMemo, useState } from "react";
import {
  Clock, Coins, RefreshCw, Plus, Trash2, Eye, EyeOff, Loader2,
  Download, UserPlus, Check, Sparkles,
} from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { ListovaniMesice } from "../../components/ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { apiFetch, useApiData } from "../../lib/api.js";
import { toast } from "../../components/Toast.jsx";
import { confirmDialog } from "../../components/confirmHost.jsx";
import InfoTip from "../../components/InfoTip.jsx";

// 3.9.2026: běžné číslice místo římských, římské se v tabulce špatně čtou.
const MESICE_ZKRATKY = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const MESICE_CS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

// Od tohoto měsíce appka počítá výplaty — dřív se dopočítat nedá.
const VYPLATY_OD = "2026-06";

const KONFIG = {
  dpp: {
    titulek: "Přehled DPP hodin",
    popis: "Odpracované hodiny na dohodu o provedení práce, po měsících.",
    ikona: Clock,
    jednotka: "h",
    jednotkaDlouha: "hodin",
    tip: "Ruční tabulka. Rok 2026 je naimportovaný z původního sheetu. Kdo má v HR DPP, přidá se sem sám. U měsíců od června 2026 jde měsíc předvyplnit z Výplat, ručně zadaná čísla se nepřepíšou.",
  },
  naklady: {
    titulek: "Náklady na lidi",
    popis: "Celkový měsíční náklad na člověka v tisících Kč, včetně skrytých položek.",
    ikona: Coins,
    jednotka: "tis.",
    jednotkaDlouha: "tisíc Kč",
    tip: "Čísla jsou v tisících Kč, takže 22000 Kč se píše jako 22. Náklad je celková odměna včetně DPP, bokem a odečtů. Každý nový aktivní člověk z HR se sem přidá sám.",
  },
};

// 1 člověk / 2 až 4 lidé / 5 a víc lidí
function lidiTvar(n) {
  if (n === 1) return "1 člověk";
  if (n >= 2 && n <= 4) return `${n} lidé`;
  return `${n} lidí`;
}

function fmtCislo(n) {
  if (n == null || n === "") return "";
  const cislo = Number(n);
  if (!Number.isFinite(cislo)) return "";
  return new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 1 }).format(cislo);
}

// Jedna buňka tabulky: klik ji přepne na input, Enter nebo blur uloží.
function Bunka({ hodnota, onSave, disabled }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState("");
  const [uklada, setUklada] = useState(false);

  function zacni() {
    if (disabled) return;
    setText(hodnota == null ? "" : String(hodnota).replace(".", ","));
    setEdit(true);
  }

  async function uloz() {
    const cisty = text.trim().replace(",", ".");
    const nova = cisty === "" ? null : Number(cisty);
    setEdit(false);
    if (cisty !== "" && !Number.isFinite(nova)) { toast("Zadej číslo.", "error"); return; }
    const stara = hodnota == null ? null : Number(hodnota);
    if (nova === stara) return;
    setUklada(true);
    try { await onSave(nova); } finally { setUklada(false); }
  }

  if (edit) {
    return (
      <td className="p-0 border-l border-slate-100">
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={uloz}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); uloz(); }
            if (e.key === "Escape") { e.preventDefault(); setEdit(false); }
          }}
          className="w-full h-9 px-1 text-center text-sm tabular-nums bg-brand-50 border-2 border-brand-500 rounded outline-none"
        />
      </td>
    );
  }

  return (
    <td
      onClick={zacni}
      title={disabled ? "" : "Klikni pro úpravu"}
      className={`text-center text-sm tabular-nums border-l border-slate-100 h-9 ${
        disabled ? "text-slate-300" : "cursor-pointer hover:bg-brand-50"
      } ${hodnota == null ? "text-slate-300" : "text-slate-800"}`}
    >
      {uklada ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto text-brand-600" /> : (hodnota == null ? "." : fmtCislo(hodnota))}
    </td>
  );
}

// Buňka v telefonním seznamu. Stejné chování jako `Bunka` v tabulce, jen to není
// `<td>` a má velkou plochu na prst (14.9.2026).
function BunkaTelefon({ hodnota, jednotka, onSave }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState("");
  const [uklada, setUklada] = useState(false);

  async function uloz() {
    const cisty = text.trim().replace(",", ".");
    const nova = cisty === "" ? null : Number(cisty);
    setEdit(false);
    if (cisty !== "" && !Number.isFinite(nova)) { toast("Zadej číslo.", "error"); return; }
    const stara = hodnota == null ? null : Number(hodnota);
    if (nova === stara) return;
    setUklada(true);
    try { await onSave(nova); } finally { setUklada(false); }
  }

  if (edit) {
    return (
      <input
        autoFocus
        inputMode="decimal"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={uloz}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); uloz(); }
          if (e.key === "Escape") { e.preventDefault(); setEdit(false); }
        }}
        className="h-11 w-24 shrink-0 rounded-xl border-2 border-brand bg-brand-50 px-2 text-center text-base font-bold tabular-nums outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setText(hodnota == null ? "" : String(hodnota).replace(".", ",")); setEdit(true); }}
      aria-label="Upravit hodnotu"
      className={`flex h-11 w-24 shrink-0 items-center justify-center rounded-xl text-base font-bold tabular-nums ${
        hodnota == null ? "border border-dashed border-slate-300 text-slate-300" : "bg-slate-100 text-slate-900"}`}
    >
      {uklada ? <Loader2 className="h-4 w-4 animate-spin text-brand-600" /> : (hodnota == null ? "—" : `${fmtCislo(hodnota)} ${jednotka}`)}
    </button>
  );
}

export default function HRRocniPrehled({ typ = "dpp" }) {
  const cfg = KONFIG[typ] || KONFIG.dpp;
  const Ikona = cfg.ikona;

  const [rok, setRok] = useState(new Date().getFullYear());
  // 14.9.2026 (fáze 5a): matice jméno krát dvanáct měsíců se na telefon nevejde.
  // Místo ní se listuje měsíc a ukáže se seznam lidí s částkou za ten jeden měsíc.
  // Otevřený je aktuální měsíc. Na počítači zůstává celá matice.
  const uzka = useUzkaObrazovka();
  const [mesic, setMesic] = useState(new Date().getMonth() + 1);
  const [otevrenyClovek, setOtevrenyClovek] = useState(null);
  const [zobrazitSkryte, setZobrazitSkryte] = useState(false);
  const [pridavam, setPridavam] = useState(false);
  const [noveJmeno, setNoveJmeno] = useState("");
  const [novePozice, setNovePozice] = useState("");
  const [doplnujiMesic, setDoplnujiMesic] = useState("");
  const [pracuji, setPracuji] = useState(false);

  const { data, loading, refetch, refreshing } = useApiData(`/api/hr-rocni-prehled?typ=${typ}&rok=${rok}`);
  const vsechnyRadky = data?.rows || [];
  const roky = data?.roky || [rok];

  const skrytychPocet = vsechnyRadky.filter((r) => r.skryto).length;
  const radky = zobrazitSkryte ? vsechnyRadky : vsechnyRadky.filter((r) => !r.skryto);

  // Součty sloupců počítáme jen z toho, co je vidět, ať sedí s tabulkou.
  const souctyMesicu = useMemo(() => {
    const out = new Array(12).fill(null);
    for (let m = 1; m <= 12; m += 1) {
      let suma = 0, mel = false;
      for (const r of radky) {
        const v = r.hodnoty ? r.hodnoty[String(m)] : null;
        if (v != null) { suma += Number(v) || 0; mel = true; }
      }
      out[m - 1] = mel ? Math.round(suma * 10) / 10 : null;
    }
    return out;
  }, [radky]);
  const soucetCelkem = Math.round(radky.reduce((s, r) => s + (Number(r.celkem) || 0), 0) * 10) / 10;
  // Součty berou jen zobrazené řádky. U nákladů je dobré vědět, kolik zůstalo
  // schované, ať se roční číslo nečte jako celkový náklad firmy.
  const skrytySoucet = Math.round(
    vsechnyRadky.filter((r) => r.skryto).reduce((s, r) => s + (Number(r.celkem) || 0), 0) * 10
  ) / 10;

  async function post(action, payload, tichy) {
    setPracuji(true);
    try {
      const res = await apiFetch("/api/hr-rocni-prehled", {
        method: "POST",
        body: JSON.stringify({ action, typ, rok, ...payload }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `Chyba ${res.status}`);
      if (!tichy) refetch();
      return j;
    } catch (e) {
      toast("Chyba: " + e.message, "error");
      return null;
    } finally {
      setPracuji(false);
    }
  }

  async function ulozBunku(personId, mesic, hodnota) {
    const r = await post("set-hodnota", { personId, mesic, hodnota }, true);
    if (r) refetch();
  }

  async function prepniSkryto(radek) {
    await post("set-skryto", { personId: radek.person_id, skryto: !radek.skryto });
  }

  async function prepniDpp(radek) {
    if (!radek.hr_id) { toast("Tenhle řádek není napojený na člověka v HR.", "error"); return; }
    const res = await apiFetch("/api/hr-rocni-prehled", {
      method: "POST",
      body: JSON.stringify({ action: "set-dpp-aktivni", personId: radek.hr_id, aktivni: !radek.dpp_aktivni }),
    });
    if (!res.ok) { toast("Nepodařilo se uložit.", "error"); return; }
    refetch();
  }

  async function smazRadek(radek) {
    const ok = await confirmDialog({
      title: "Smazat řádek",
      description: `Opravdu smazat ${radek.jmeno} z přehledu za rok ${rok}? Zadaná čísla se ztratí. U aktivního člena týmu se navíc řádek zase sám vrátí, protože se lidé z HR doplňují automaticky. Na trvalé schování použij skrytí.`,
      confirmLabel: "Smazat",
    });
    if (!ok) return;
    await post("smazat-radek", { personId: radek.person_id });
  }

  async function pridejRadek() {
    const jmeno = noveJmeno.trim();
    if (!jmeno) { toast("Zadej jméno.", "error"); return; }
    const osoba = (data?.lide || []).find((l) => l.display_name === jmeno);
    const r = await post("set-radek", {
      jmeno,
      pozice: novePozice.trim() || (osoba ? osoba.pozice : ""),
      personId: osoba ? osoba.id : undefined,
    });
    if (r) { setNoveJmeno(""); setNovePozice(""); setPridavam(false); }
  }

  async function doplnLidiZHr() {
    const r = await post("sync-lide", {});
    if (r) toast(r.pridano > 0 ? `Přidáno ${r.pridano} lidí z HR.` : "Všichni aktivní lidé už v přehledu jsou.", r.pridano > 0 ? "success" : "info");
  }

  // Předvyplnění měsíce z Výplat. Bere data z /api/hr-vyplaty, takže tu
  // není žádná druhá verze výpočtu — jen se přepíšou do přehledu.
  async function doplnZVyplat(mesicKlic) {
    if (!mesicKlic) return;
    const mesicCislo = parseInt(mesicKlic.split("-")[1], 10);
    setPracuji(true);
    try {
      const res = await apiFetch(`/api/hr-vyplaty?mesic=${mesicKlic}`);
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `Chyba ${res.status}`);
      const rows = j.rows || [];
      let hodnoty;
      if (typ === "naklady") {
        // Celkový náklad = celková odměna včetně skrytých kanálů, v tisících.
        hodnoty = rows.map((r) => ({
          personId: r.id, jmeno: r.display_name, pozice: r.pozice || "",
          hodnota: Math.round((Number(r.celkem) || 0) / 1000),
        }));
      } else {
        // DPP hodiny — z exportu pro účetní, ten už umí i režim hodiny/sales.
        const podleId = new Map();
        for (const d of (j.dppExport || [])) {
          if (d.id) podleId.set(d.id, d.hours);
        }
        hodnoty = rows
          .filter((r) => podleId.has(r.id) || r.dpp_hodiny != null)
          .map((r) => ({
            personId: r.id, jmeno: r.display_name, pozice: r.pozice || "",
            hodnota: podleId.has(r.id) ? podleId.get(r.id) : Number(r.dpp_hodiny),
          }));
      }
      hodnoty = hodnoty.filter((h) => Number.isFinite(h.hodnota));
      if (!hodnoty.length) { toast("Za tenhle měsíc nejsou ve Výplatách žádná čísla.", "error"); return; }
      const r = await post("bulk-mesic", { mesic: mesicCislo, hodnoty }, true);
      if (r) {
        refetch();
        const casti = [`Doplněno ${r.zapsano}`];
        if (r.preskoceno) casti.push(`${r.preskoceno} ponecháno ručně`);
        if (r.nove) casti.push(`${r.nove} nových řádků`);
        toast(casti.join(", ") + ".", "success");
      }
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    } finally {
      setPracuji(false);
      setDoplnujiMesic("");
    }
  }

  function exportCsv() {
    const hlavicka = ["Jméno", ...(typ === "naklady" ? ["Pozice"] : []), ...MESICE_ZKRATKY.map((_, i) => i + 1), "Celkem"];
    const lines = [hlavicka.join(",")];
    for (const r of radky) {
      const bunky = [];
      for (let m = 1; m <= 12; m += 1) {
        const v = r.hodnoty ? r.hodnoty[String(m)] : null;
        bunky.push(v == null ? "" : String(v));
      }
      const jmeno = /[",]/.test(r.jmeno) ? `"${r.jmeno.replace(/"/g, '""')}"` : r.jmeno;
      lines.push([jmeno, ...(typ === "naklady" ? [r.pozice || ""] : []), ...bunky, String(r.celkem)].join(","));
    }
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${typ === "dpp" ? "DPP-hodiny" : "Naklady-na-lidi"}-${rok}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Měsíce, které jde předvyplnit z Výplat: od VYPLATY_OD po aktuální měsíc.
  const dostupneMesice = useMemo(() => {
    const out = [];
    const ted = new Date();
    for (let m = 1; m <= 12; m += 1) {
      const klic = `${rok}-${String(m).padStart(2, "0")}`;
      if (klic < VYPLATY_OD) continue;
      if (rok > ted.getFullYear() || (rok === ted.getFullYear() && m > ted.getMonth() + 1)) continue;
      out.push({ klic, cislo: m, label: `${MESICE_CS[m - 1]} ${rok}` });
    }
    return out;
  }, [rok]);

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      {/* 9.9.2026 (fáze 4c): hlavička je stejná gradientová lišta jako na
          ostatních stránkách, obnovení je ikona. */}
      <GradientHeader
        title={cfg.titulek}
        icon={Ikona}
        loading={refreshing}
        napoveda={`${cfg.popis} ${cfg.tip}`}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing }]}
      />

      {/* 12.9.2026 (fáze 4c): výběr roku vypadl z tmavé hlavičky ven na stránku,
          stejně jako listování měsíci u Výplat. */}
      <ListovaniMesice
        popis={String(rok)}
        onZpet={() => setRok(rok - 1)}
        onVpred={() => setRok(rok + 1)}
        zpetVypnuto={rok <= Math.min(...roky)}
        vpredVypnuto={rok >= Math.max(...roky)}
        naDnesek={rok !== new Date().getFullYear() ? () => setRok(new Date().getFullYear()) : undefined}
        popisDneska="Na letošek"
      />

      {uzka && (
        <ListovaniMesice
          popis={MESICE_CS[mesic - 1]}
          onZpet={() => setMesic((m) => Math.max(1, m - 1))}
          onVpred={() => setMesic((m) => Math.min(12, m + 1))}
          zpetVypnuto={mesic <= 1}
          vpredVypnuto={mesic >= 12}
          naDnesek={
            (mesic !== new Date().getMonth() + 1 || rok !== new Date().getFullYear())
              ? () => { setRok(new Date().getFullYear()); setMesic(new Date().getMonth() + 1); }
              : undefined
          }
          popisDneska="Na tento měsíc"
        />
      )}

      {/* Ovládání */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setPridavam((v) => !v)}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4" />
          Přidat řádek
        </button>
        <button
          onClick={doplnLidiZHr}
          disabled={pracuji}
          className="btn btn-secondary"
        >
          <UserPlus className="w-4 h-4" />
          Doplnit lidi z HR
        </button>

        {dostupneMesice.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="w-px h-5 bg-slate-200" />
            <select
              value={doplnujiMesic}
              onChange={(e) => { setDoplnujiMesic(e.target.value); doplnZVyplat(e.target.value); }}
              disabled={pracuji}
              className="field w-auto h-9"
            >
              <option value="">Doplnit z výplat: vyber měsíc</option>
              {dostupneMesice.map((m) => <option key={m.klic} value={m.klic}>{m.label}</option>)}
            </select>
            <InfoTip text="Načte měsíc z Výplat a doplní jen prázdné buňky. Ručně zadaná čísla zůstanou beze změny. Kdo je skrytý a vyjde mu nenulové číslo, se sám odkryje." />
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {skrytychPocet > 0 && (
            <button
              onClick={() => setZobrazitSkryte((v) => !v)}
              className="btn btn-secondary"
            >
              {zobrazitSkryte ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {zobrazitSkryte ? "Schovat skryté" : `Zobrazit skryté (${skrytychPocet})`}
            </button>
          )}
          <button
            onClick={exportCsv}
            className="btn btn-secondary"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
        </div>
      </div>

      {/* Formulář nového řádku */}
      {pridavam && (
        <div className="bg-brand-50 rounded-xl border border-brand-200 p-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Jméno</label>
            <input
              list="rp-lide"
              value={noveJmeno}
              onChange={(e) => setNoveJmeno(e.target.value)}
              placeholder="Vyber z HR nebo napiš ručně"
              className="field w-full h-9"
            />
            <datalist id="rp-lide">
              {(data?.lide || []).map((l) => <option key={l.id} value={l.display_name} />)}
            </datalist>
          </div>
          <div className="w-48">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Pozice</label>
            <input
              value={novePozice}
              onChange={(e) => setNovePozice(e.target.value)}
              placeholder="Nepovinné"
              className="field w-full h-9"
            />
          </div>
          <button
            onClick={pridejRadek}
            disabled={pracuji}
            className="btn btn-primary"
          >
            <Check className="w-4 h-4" />
            Přidat
          </button>
          <button
            onClick={() => { setPridavam(false); setNoveJmeno(""); setNovePozice(""); }}
            className="btn btn-secondary"
          >
            Zrušit
          </button>
        </div>
      )}

      {/* Tabulka */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Načítám.
          </div>
        ) : radky.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            Za rok {rok} tu zatím nic není. Přidej řádek nebo doplň lidi z HR.
          </div>
        ) : uzka ? (
          <>
            <div className="divide-y divide-slate-100">
              {radky.map((r) => {
                const otevreny = otevrenyClovek === r.person_id;
                return (
                  <div key={r.person_id} className={r.skryto ? "bg-slate-50/70 opacity-60" : ""}>
                    <div className="flex items-center gap-2 px-3 py-2.5">
                      <button type="button" onClick={() => setOtevrenyClovek(otevreny ? null : r.person_id)}
                        aria-expanded={otevreny} className="min-w-0 flex-1 text-left">
                        <span className="block truncate text-base font-bold leading-tight text-slate-900">{r.jmeno}</span>
                        <span className="block truncate text-[13px] text-slate-500">
                          {r.pozice || "—"}
                          {r.skryto ? ", skryto" : ""}
                        </span>
                      </button>
                      <BunkaTelefon
                        hodnota={r.hodnoty ? r.hodnoty[String(mesic)] : null}
                        jednotka={cfg.jednotka}
                        onSave={(v) => ulozBunku(r.person_id, mesic, v)}
                      />
                    </div>
                    {otevreny && (
                      <div className="flex flex-wrap gap-2 px-3 pb-3">
                        <span className="inline-flex min-h-[40px] items-center rounded-xl bg-slate-100 px-3 text-[13px] font-semibold tabular-nums text-slate-600">
                          za rok {fmtCislo(r.celkem)} {cfg.jednotka}
                        </span>
                        {typ === "dpp" && (
                          <button type="button" onClick={() => prepniDpp(r)} disabled={!r.hr_id}
                            className={`inline-flex min-h-[40px] items-center rounded-xl px-3 text-[13px] font-semibold ${
                              !r.hr_id ? "bg-slate-100 text-slate-400"
                                : r.dpp_aktivni ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {r.dpp_aktivni ? "DPP aktivní" : "DPP ne"}
                          </button>
                        )}
                        <button type="button" onClick={() => prepniSkryto(r)}
                          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-slate-200 px-3 text-[13px] font-semibold text-slate-700">
                          {r.skryto ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          {r.skryto ? "Odkrýt" : "Skrýt"}
                        </button>
                        <button type="button" onClick={() => smazRadek(r)}
                          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-rose-200 px-3 text-[13px] font-semibold text-rose-600">
                          <Trash2 className="h-4 w-4" /> Smazat
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2 border-t-2 border-slate-200 bg-slate-50 px-4 py-2.5">
              <span className="flex-1 text-sm font-bold text-slate-900">Celkem za {MESICE_CS[mesic - 1].toLowerCase()}</span>
              <span className="text-base font-bold tabular-nums text-slate-900">
                {souctyMesicu[mesic - 1] == null ? "—" : `${fmtCislo(souctyMesicu[mesic - 1])} ${cfg.jednotka}`}
              </span>
            </div>
          </>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[190px]">
                    Jméno
                  </th>
                  {typ === "dpp" && (
                    <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide px-2 py-2 whitespace-nowrap">
                      DPP
                    </th>
                  )}
                  {MESICE_ZKRATKY.map((z, i) => (
                    <th key={z} title={MESICE_CS[i]} className="text-center text-xs font-semibold text-slate-500 px-1 py-2 border-l border-slate-200 w-12">
                      {z}
                    </th>
                  ))}
                  <th className="text-center text-xs font-semibold text-slate-700 uppercase tracking-wide px-2 py-2 border-l-2 border-slate-300 w-20">
                    Celkem
                  </th>
                  <th className="w-16" />
                </tr>
              </thead>
              <tbody>
                {radky.map((r) => (
                  <tr key={r.person_id} className={`border-b border-slate-100 group ${r.skryto ? "bg-slate-50/70 opacity-60" : "hover:bg-slate-50/50"}`}>
                    <td className="px-3 py-1.5 sticky left-0 bg-white group-hover:bg-slate-50/50 z-10">
                      <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                        {r.jmeno}
                        {r.skryto && (
                          <span className="text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                            Skryto
                          </span>
                        )}
                      </div>
                      {r.pozice && <div className="text-xs text-slate-500 truncate">{r.pozice}</div>}
                    </td>
                    {typ === "dpp" && (
                      <td className="text-center px-2">
                        <button
                          onClick={() => prepniDpp(r)}
                          disabled={!r.hr_id}
                          title={r.hr_id ? (r.dpp_aktivni ? "Má aktivní DPP, klikni pro vypnutí" : "Nemá aktivní DPP, klikni pro zapnutí") : "Řádek není napojený na člověka v HR"}
                          className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide ${
                            !r.hr_id ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                              : r.dpp_aktivni ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                        >
                          {r.dpp_aktivni ? "Aktivní" : "Ne"}
                        </button>
                      </td>
                    )}
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <Bunka
                        key={m}
                        hodnota={r.hodnoty ? r.hodnoty[String(m)] : null}
                        onSave={(v) => ulozBunku(r.person_id, m, v)}
                      />
                    ))}
                    <td className="text-center text-sm font-bold text-slate-900 tabular-nums border-l-2 border-slate-300">
                      {fmtCislo(r.celkem)}
                    </td>
                    <td className="text-right pr-2">
                      <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => prepniSkryto(r)}
                          title={r.skryto ? "Odkrýt" : "Skrýt ze seznamu"}
                          className="p-1.5 rounded text-slate-400 hover:text-brand-600 hover:bg-brand-50"
                        >
                          {r.skryto ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => smazRadek(r)}
                          title="Smazat řádek"
                          className="p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-300">
                  <td className="px-3 py-2 sticky left-0 bg-slate-50 z-10 text-sm font-bold text-slate-900">
                    Celkem ({cfg.jednotkaDlouha})
                  </td>
                  {typ === "dpp" && <td />}
                  {souctyMesicu.map((s, i) => (
                    <td key={i} className="text-center text-sm font-bold text-slate-700 tabular-nums border-l border-slate-200 py-2">
                      {s == null ? "" : fmtCislo(s)}
                    </td>
                  ))}
                  <td className="text-center text-sm font-bold text-brand-700 tabular-nums border-l-2 border-slate-300">
                    {fmtCislo(soucetCelkem)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <p className="text-xs text-slate-400">
          Buňku upravíš kliknutím. Prázdná buňka znamená nevyplněno, nula je nula.
          {typ === "naklady" ? " Čísla jsou v tisících Kč." : " Čísla jsou v hodinách."}
          {" "}Jméno a pozice se u lidí z HR berou z jejich profilu. Nový aktivní člověk
          se přidá sám a skrytý řádek se sám odkryje, jakmile v něm přibude nenulové číslo.
        </p>
        {skrytychPocet > 0 && !zobrazitSkryte && (
          <p className="text-xs text-amber-600">
            Součty počítají jen zobrazené řádky. Skryto je {lidiTvar(skrytychPocet)} se součtem{" "}
            {fmtCislo(skrytySoucet)} {cfg.jednotka}
          </p>
        )}
      </div>
    </div>
  );
}
